import { Injectable, inject, Injector } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, firstValueFrom, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  Questionnaire,
  CreateQuestionnaireRequest,
  ContractChecklist,
  QuestionnaireStatus,
  QuestionnaireFilechecklistStepResponse,
  ApiResponse,
  QuestionnaireAnswerResponse,
  // Assicurati che i modelli sotto siano aggiornati nel file models.ts
} from '../models/models';
import { AuthService } from './auth.service';
import { ProfessionalsDataService } from '../../frontoffice/services/professionals-data.service';
import { environment } from '../../../environments/environment';
import { FileChecklistStatusDTO } from '../models/file-checklist.model';
import { FileUploadAnswerDTO } from '../models/file-upload-answer.model';

export interface QuestionnairePdfHeaderData {
  businessName: string;
  professionalType: string;
  solCode: string;
  vatNumber: string;
}

@Injectable({
  providedIn: 'root',
})
export class QuestionnaireService {
  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthService);
  private readonly injector = inject(Injector);
  private readonly baseUrl = `${environment.coreApiUrl}/api/v1/questionnaires`;
  private readonly contractChecklistBaseUrl = `${environment.coreApiUrl}/api/v1/filechecklists`;
  private readonly fileUploadBaseUrl = `${environment.coreApiUrl}/api/v1/fileupload`;

  // ============================================
  // QUESTIONNAIRES (Tag: questionnaires)
  // ============================================

  /**
   * OperationId: listQuestionnaires
   * Summary: Lista questionari
   */
  listQuestionnaires(): Observable<Questionnaire[]> {
    return this.http
      .get<any>(this.baseUrl)
      .pipe(map((res) => this.unwrapArray<Questionnaire>(res)));
  }

  listQuestionnairesWithDeleted(): Observable<Questionnaire[]> {
    return this.http
      .get<any>(`${this.baseUrl}?includeDeleted=true`)
      .pipe(map((res) => this.unwrapArray<Questionnaire>(res)));
  }

  listQuestionnairesByCurrentTrusteeType(): Observable<Questionnaire[]> {
    const trusteeType = this.getCurrentTrusteeType();
    if (!trusteeType) {
      return of([]);
    }
    return this.http
      .get<any>(`${this.baseUrl}?trusteeType=${encodeURIComponent(trusteeType)}`)
      .pipe(map((res) => this.unwrapArray<Questionnaire>(res)));
  }

  listQuestionnairesByCurrentProfessionalAndTrusteeType(): Observable<QuestionnaireStatus[]> {
    const trusteeType = this.getCurrentTrusteeType();
    const professionalId = this.getCurrentProfessionalId();

    if (!trusteeType || !professionalId) {
      return of([]);
    }

    const url = `${this.baseUrl}/status/${encodeURIComponent(professionalId)}?trusteeType=${encodeURIComponent(trusteeType)}`;

    return this.http
      .get<any>(url)
      .pipe(map((res) => this.unwrapArray<QuestionnaireStatus>(res)));
  }

  /**
   * 📋 Lista questionari per il professional e la tipologia specificati
   * @param professionalId ID del fiduciario
   * @param trusteeType Tipologia del fiduciario
   * @returns Observable<QuestionnaireStatus[]>
   */
  listQuestionnairesByProfessionalAndTrusteeType(
    professionalId: string, 
    trusteeType: string
  ): Observable<QuestionnaireStatus[]> {
    if (!trusteeType || !professionalId) {
      return of([]);
    }

    const url = `${this.baseUrl}/status/${encodeURIComponent(professionalId)}?trusteeType=${encodeURIComponent(trusteeType)}`;

    return this.http
      .get<any>(url)
      .pipe(map((res) => this.unwrapArray<QuestionnaireStatus>(res)));
  }

  /**
   * OperationId: createQuestionnaire
   * Summary: Crea un questionario
   */
  createQuestionnaire(req: CreateQuestionnaireRequest): Observable<Questionnaire> {
    return this.http
      .post<any>(this.baseUrl, req)
      .pipe(map((res) => this.unwrapItem<Questionnaire>(res)));
  }

  /**
   * OperationId: getQuestionnaireById
   * Summary: Ottieni questionario per id
   * @param id format: uuid
   */
  getQuestionnaireById(id: string): Observable<Questionnaire> {
    return this.http
      .get<any>(`${this.baseUrl}/${id}`)
      .pipe(map((res) => this.unwrapItem<Questionnaire>(res)));
  }

  /**
   * OperationId: updateQuestionnaire
   * Summary: Aggiorna questionario
   * @param id format: uuid
   */
  updateQuestionnaire(id: string, req: CreateQuestionnaireRequest): Observable<Questionnaire> {
    return this.http
      .put<any>(`${this.baseUrl}/${id}`, req)
      .pipe(map((res) => this.unwrapItem<Questionnaire>(res)));
  }

  /**
   * OperationId: deleteQuestionnaire
   * Summary: Elimina questionario
   * @param id format: uuid
   */
  deleteQuestionnaire(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}`);
  }

  deleteChecklist(id: string): Observable<void> {
    return this.http.delete<void>(`${this.contractChecklistBaseUrl}/${id}`);
  }

  // ============================================
  // RESPONSES (Tag: responses)
  // ============================================

  /**
   * OperationId: submitAnswers
   * Summary: Sottometti le risposte per una istanza di questionario (validazione inclusa)
   * Path: /api/questionnaires/answers
   */
  submitAnswers(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/answers`, payload, {
      responseType: 'text', // <--- Questo impedisce ad Angular di cercare un JSON
    });
  }

  /**
   * Get saved answers by instance ID
   * Endpoint: GET /api/v1/questionnaires/answers/instance/{instanceId}
   */
  getAnswersByInstanceId(instanceId: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/answers/instance/${instanceId}`);
  }

  /**
   * Get all file checklists (not deleted)
   */
  getFileChecklists(): Observable<any[]> {
    return this.http
      .get<any>(this.contractChecklistBaseUrl)
      .pipe(map((res) => this.unwrapArray(res)));
  }

  getFileChecklistsById(id: string): Observable<any> {
    return this.http
      .get<any>(`${this.contractChecklistBaseUrl}/${id}`)
      .pipe(map((res) => this.unwrapItem(res)));
  }

  /**
   * Get all file checklists, including deleted
   */
  getFileChecklistsWithDeleted(): Observable<any[]> {
    return this.http
      .get<any>(`${this.contractChecklistBaseUrl}?includeDeleted=true`)
      .pipe(map((res) => this.unwrapArray(res)));
  }

  getFileChecklistsByCurrentTrusteeType(): Observable<any[]> {
    const trusteeType = this.getCurrentTrusteeType();
    if (!trusteeType) {
      return of([]);
    }
    return this.http
      .get<any>(`${this.contractChecklistBaseUrl}?trusteeType=${encodeURIComponent(trusteeType)}`)
      .pipe(map((res) => this.unwrapArray(res)));
  }

  /**
   * Recupera lo stato della checklist (Richieste + Risposte del fiduciario)
   */
  getFileChecklistStatus(professionalId: string): Observable<FileChecklistStatusDTO[]> {
    const trusteeType = this.getCurrentTrusteeType();
    
    if (!trusteeType || !professionalId) {
      return of([]);
    }

    const url = `${this.contractChecklistBaseUrl}/status`;

    return this.http
      .get<any>(url, {
        params: { professionalId, trusteeType },
      })
      .pipe(map((res) => this.unwrapArray<FileChecklistStatusDTO>(res)));
  }

  /**
   * Get the file checklist status for specified professional and trustee type
   * @param professionalId ID del fiduciario
   * @param trusteeType Tipologia del fiduciario
   */
  getFileChecklistStatusByProfessionalAndTrusteeType(
    professionalId: string,
    trusteeType: string
  ): Observable<FileChecklistStatusDTO[]> {
    if (!professionalId || !trusteeType) {
      return of([]);
    }

    const url = `${this.contractChecklistBaseUrl}/status`;

    return this.http
      .get<any>(url, {
        params: { professionalId, trusteeType },
      })
      .pipe(map((res) => this.unwrapArray<FileChecklistStatusDTO>(res)));
  }

  /**
   * Salva il riferimento del file caricato nel DB (tabella file_upload_answers)
   */
  /**
   * @deprecated ENDPOINT DEPRECATO - Non più necessario con il nuovo flusso upload
   * Il nuovo endpoint /upload gestisce sia l'upload S3 che il salvataggio DB
   * Mantenuto temporaneamente per retrocompatibilità
   */
  saveFileAnswer(payload: FileUploadAnswerDTO): Observable<void> {
    return this.http.post<void>(`${this.contractChecklistBaseUrl}/answers`, payload);
  }

  /**
   * OperationId: createContractChecklist
   * Summary: Crea una checklist documentale per contratto
   */
  createContractChecklist(req: ContractChecklist): Observable<any> {
    return this.http.post<any>(this.contractChecklistBaseUrl, req);
  }

  /**
   * OperationId: updateContractChecklist
   * Summary: Aggiorna una checklist documentale per contratto
   * @param id format: uuid
   */
  updateContractChecklist(id: string, req: ContractChecklist): Observable<any> {
    return this.http.put<any>(`${this.contractChecklistBaseUrl}/${id}`, req);
  }

  /**
   * Crea una checklist con file template allegati (multipart/form-data)
   *
   * Mapping file → item: la chiave FormData `templateFile_N` corrisponde
   * all'elemento `fileUploadRequests[N]` nel JSON (indice 0-based).
   * Gli indici senza file non vengono inviati; il backend deve trattarli come null.
   *
   * @param req Dati della checklist
   * @param templateFiles Array parallelo a fileUploadRequests; null = nessun template per quell'item
   */
  createContractChecklistWithFiles(req: ContractChecklist, templateFiles: (File | null)[]): Observable<any> {
    const formData = new FormData();
    formData.append('checklist', JSON.stringify(req));

    templateFiles.forEach((file, index) => {
      if (file) {
        // chiave templateFile_N → fileUploadRequests[N]
        formData.append(`templateFile_${index}`, file, file.name);
      }
    });

    return this.http.post<any>(this.contractChecklistBaseUrl, formData);
  }

  /**
   * Aggiorna una checklist con file template allegati (multipart/form-data)
   *
   * Stesso mapping di createContractChecklistWithFiles.
   * Se per un item esistente non arriva nessun file, il backend mantiene il template già salvato,
   * a meno che `removeTemplateFlags[N]` sia true → il backend deve eliminare il template di quell'item.
   *
   * @param id ID della checklist da aggiornare
   * @param req Dati della checklist
   * @param templateFiles Array parallelo a fileUploadRequests; null = nessun nuovo file per quell'item
   * Se `isTemplateFilePresent=false` nel body JSON, il BE elimina il template da S3 automaticamente.
   */
  updateContractChecklistWithFiles(id: string, req: ContractChecklist, templateFiles: (File | null)[]): Observable<any> {
    const formData = new FormData();
    formData.append('checklist', JSON.stringify(req));

    templateFiles.forEach((file, index) => {
      if (file) {
        // chiave templateFile_N → fileUploadRequests[N]
        formData.append(`templateFile_${index}`, file, file.name);
      }
    });

    // Nota: la rimozione del template è gestita via isTemplateFilePresent=false nel payload JSON
    // Non serve inviare removeTemplate_N separatamente

    return this.http.put<any>(`${this.contractChecklistBaseUrl}/${id}`, formData);
  }

  /**
   * Upload a file to S3 via fileupload API
   * NUOVO FLUSSO: carica direttamente in posizione finale + salva DB in un'unica chiamata
   * @param file The file to upload
   * @param professionalId ID del fiduciario
   * @param requestId UUID della FileUploadRequest
   * @param expirationDate Data di scadenza opzionale (formato YYYY-MM-DD)
   */
  uploadFileToS3(
    file: File,
    professionalId: string,
    requestId: string,
    expirationDate?: string | null
  ): Promise<string> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    formData.append('professionalId', professionalId);
    formData.append('requestId', requestId);
    formData.append('trusteeType', this.getCurrentTrusteeType() ?? '');

    // Aggiungi data scadenza se presente
    if (expirationDate) {
      formData.append('expirationDate', expirationDate);
    }

    return firstValueFrom(
      this.http.post<{ fileUrl: string }>(
        `${this.contractChecklistBaseUrl}/upload`, // POST /api/v1/filechecklists/upload
        formData,
      ),
    ).then((response) => response?.fileUrl || '');
  }

  getChecklistById(id: string): Observable<any> {
    return this.getFileChecklistsById(id);
  }

  downloadPdf(
    questionnaireId: string,
    instanceId: string,
    headerData?: QuestionnairePdfHeaderData,
  ): Observable<Blob> {
    const url = `${this.baseUrl}/answers/${questionnaireId}/${instanceId}/pdf`;

    if (headerData) {
      return this.http.post(url, headerData, {
        responseType: 'blob',
        observe: 'body',
      });
    }

    return this.http.get(url, {
      responseType: 'blob', // CRITICAL: Tells Angular this is a file, not JSON
      observe: 'body',
    });
  }

  getQuestionnaireAnswers(instanceId: string): Observable<QuestionnaireAnswerResponse> {
    const url = `${this.baseUrl}/answers/instance/${instanceId}`;

    return this.http.get<any>(url).pipe(
      map((res) => this.unwrapItem(res) as QuestionnaireAnswerResponse),
      catchError((error) => {
        if (error?.status === 404 || error?.status === 400) {
          return throwError(() => new Error('Unable to retrieve questionnaire answers from backend'));
        }
        return throwError(() => error);
      }),
    );
  }

  /**
   * Download a file checklist document via backend API
   * @param professionalId ID del fiduciario
   * @param requestId UUID della FileUploadRequest
   * @returns Observable<Blob> con il file
   */
  downloadFileChecklist(professionalId: string, requestId: string): Observable<Blob> {
    const url = `${this.contractChecklistBaseUrl}/download?professionalId=${professionalId}&requestId=${requestId}`;

    return this.http.get(url, {
      responseType: 'blob',
      observe: 'body',
    });
  }

  /**
   * Download del file template/esempio di una FileUploadRequest della checklist
   * @param checklistId ID della checklist
   * @param fileUploadRequestId UUID della FileUploadRequest
   * @returns Observable<Blob> con il file template
   */
  downloadChecklistTemplate(itemId: string): Observable<Blob> {
    const url = `${this.contractChecklistBaseUrl}/items/${itemId}/template`;

    return this.http.get(url, {
      responseType: 'blob',
      observe: 'body',
    });
  }

  /**
   * Aggiorna solo la data di scadenza di un documento checklist già caricato.
   * @param answerId ID della risposta (FileUploadAnswer)
   * @param expirationDate Data in formato YYYY-MM-DD
   */
  updateChecklistItemExpiration(answerId: string, expirationDate: string): Observable<any> {
    return this.http.patch<any>(
      `${this.contractChecklistBaseUrl}/answers/${answerId}/expiration-date`,
      { expirationDate },
    );
  }

  /**
   * Recupera lo stato dello step dei questionari/allegati dal microservizio pal-dmn-core
   * @param professionalId ID del fiduciario
   * @returns Observable con la risposta dello step
   */
  getQuestionnaireStep(professionalId: string): Observable<ApiResponse<QuestionnaireFilechecklistStepResponse>> {
    // Assicurati che l'URL punti alla porta o al context-path corretto di pal-dmn-core
    return this.http.get<ApiResponse<QuestionnaireFilechecklistStepResponse>>(
      `${this.baseUrl}/questionnaire-steps/${professionalId}`
    );
  }

  /** Unwraps { status, message, data: T[] } → T[], or returns the response as-is if already an array */
  private unwrapArray<T>(res: any): T[] {
    if (Array.isArray(res)) return res as T[];
    if (res && Array.isArray(res.data)) return res.data as T[];
    return [];
  }

  /** Unwraps { status, message, data: T } → T, or returns the response as-is */
  private unwrapItem<T>(res: any): T {
    if (res && 'data' in res && !Array.isArray(res.data)) return res.data as T;
    return res as T;
  }

  private getCurrentProfessionalId(): string | null {
    return this.authService.getCurrentUser()?.professionalId || sessionStorage.getItem('professional_id');
  }

  getCurrentTrusteeType(): string | null {
    const professionalsDataService = this.injector.get(ProfessionalsDataService);
    return professionalsDataService.getTipologiaFiduciario() || null;
  }

}
